import React, { useEffect, useState } from "react";

/**
 * DevisDetailModal
 * ----------------
 * Modal admin : saisie du devis + impression du bon de travail complet
 * (3 pages) au format Laforce Group.
 *
 * Total = taux × (heures de travail + heures de déplacement) + frais + carburant
 * Les sacs à matelas sont indiqués en quantité, sans montant facturé.
 */

// Valeurs de repli si /company-config/latest est indisponible
const ENTREPRISE_DEFAUT = {
  logo: "",
  name: "Samyy's Haul",
  slogan: "Déménagement résidentiel et commercial",
  address: "",
  phone: "",
  email: "",
};

const INTERAC = "Laforce514";

const CHAMPS = [
  { name: "prixParHeure", label: "Prix par heure", unite: "$ / h", step: "0.01", requis: true },
  {
    name: "tempsDeplacement",
    label: "Temps de travail",
    unite: "heures",
    step: "0.25",
    aide: "Tranches de 15 min, facturé au taux horaire",
  },
  {
    name: "heuresDeplacement",
    label: "Temps de déplacement",
    unite: "heures",
    step: "0.25",
    aide: "Trajet, facturé au même taux horaire",
  },
  { name: "fraisDeplacement", label: "Frais de déplacement", unite: "$", step: "0.01" },
  { name: "fraisCarburant", label: "Frais de carburant", unite: "$", step: "0.01" },
  {
    name: "sacMatelas",
    label: "Sacs à matelas",
    unite: "sacs",
    step: "1",
    aide: "Quantité",
  },
];

const DevisDetailModal = ({ demande, apiBaseUrl, onClose, onSaved }) => {
  const [form, setForm] = useState({
    prixParHeure: demande.prixParHeure ?? "",
    tempsDeplacement: demande.tempsDeplacement ?? "",
    heuresDeplacement: demande.heuresDeplacement ?? "",
    fraisCarburant: demande.fraisCarburant ?? "",
    fraisDeplacement: demande.fraisDeplacement ?? "",
    sacMatelas: demande.sacMatelas ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState("");
  const [societe, setSociete] = useState(ENTREPRISE_DEFAUT);

  // Coordonnées et logo de l'entreprise, chargés depuis la config société
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/company-config/latest`);
        if (!res.ok) return;
        const data = await res.json();
        // Selon l'enveloppe de réponse utilisée par l'API
        const config = data?.data?.companyConfig ?? data?.data ?? data;
        if (!annule && config?.name) {
          setSociete({ ...ENTREPRISE_DEFAUT, ...config });
        }
      } catch {
        // On garde les valeurs de repli, l'impression reste possible
      }
    })();
    return () => {
      annule = true;
    };
  }, [apiBaseUrl]);

  const num = (v) =>
    v === "" || v === null || v === undefined || isNaN(v) ? 0 : parseFloat(v);
  const arrondi = (n) => Math.round(n * 100) / 100;

  const taux = num(form.prixParHeure);
  const tempsTravail = arrondi(taux * num(form.tempsDeplacement));
  const coutDeplacement = arrondi(taux * num(form.heuresDeplacement));
  // sacMatelas est une quantité, pas un montant
  const nbSacs = Math.max(0, Math.floor(num(form.sacMatelas)));
  const total = arrondi(
    tempsTravail +
      coutDeplacement +
      num(form.fraisDeplacement) +
      num(form.fraisCarburant)
  );

  const fmt = (n) =>
    (Number(n) || 0).toLocaleString("fr-CA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErreur("");
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const valider = () => {
    if (num(form.prixParHeure) <= 0) {
      setErreur("Saisissez un prix par heure supérieur à 0.");
      return false;
    }
    if (total <= 0) {
      setErreur("Le total doit être supérieur à 0.");
      return false;
    }
    return true;
  };

  // --- Mapping des champs réels de l'entité Demande (l'id est un UUID) ---
  const numeroBon = demande.numeroBonTravail ?? "";
  const reference = String(demande.id || "").slice(0, 8).toUpperCase();
  const dateBon = new Date().toLocaleDateString("fr-CA");

  const nomClient =
    [demande.clientFirstName, demande.clientLastName].filter(Boolean).join(" ") ||
    [demande.user?.firstName, demande.user?.lastName].filter(Boolean).join(" ") ||
    "";
  const emailClient = demande.clientEmail || demande.user?.email || "";
  const telClient = demande.clientPhone || demande.user?.phone || "";
  const adresseOrigine =
    demande.departureAddress?.fullAddress || demande.pickupAddress?.fullAddress || "";
  const adresseDestination = demande.arrivalAddress?.fullAddress || "";
  const descriptionTravail =
    demande.descriptionManuelle || demande.notes || demande.serviceType || "";
  const dateService = demande.serviceDate
    ? new Date(demande.serviceDate).toLocaleDateString("fr-CA")
    : "";


  // ============================================================
  // DOCUMENT IMPRIMABLE — 3 pages, format Laforce Group
  // avecInterne : inclut la section « Réservé à l'usage interne »
  // ============================================================
  const ligneArticle = (qte, description, prixUnite, ligneTotal) => `
    <tr>
      <td class="c">${qte === "" || qte === 0 ? "" : qte}</td>
      <td>${description}</td>
      <td class="m">${prixUnite === null ? "" : fmt(prixUnite) + " $"}</td>
      <td class="m">${ligneTotal === null ? "" : fmt(ligneTotal) + " $"}</td>
    </tr>`;

  const ligneVide = () => ligneArticle("", "&nbsp;", null, null);

  const documentHtml = (avecInterne = true) => `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8">
<title>Bon de travail ${numeroBon || reference} — ${societe.name}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #000;
         margin: 0; padding: 26px; background: #fff; }
  .page { max-width: 760px; margin: 0 auto 40px; }
  .page + .page { border-top: 1px dashed #bbb; padding-top: 32px; }

  /* --- En-tête --- */
  header { display: flex; justify-content: space-between; align-items: flex-start;
           margin-bottom: 20px; }
  .logo { max-width: 190px; max-height: 90px; display: block; margin-bottom: 8px; }
  .marque { font-size: 24px; font-weight: 800; letter-spacing: -0.5px; }
  .slogan { font-size: 10px; color: #444; margin-top: 2px; }
  .coord { margin-top: 12px; line-height: 1.7; font-weight: 600; }
  .droite { width: 280px; }
  .droite h1 { font-size: 21px; font-weight: 700; margin: 0 0 14px; text-align: center; }
  .champ { margin-bottom: 10px; }
  .champ .lbl { background: #c9d7ea; text-align: center; font-weight: 700;
                font-size: 10px; padding: 3px; }
  .champ .val { text-align: center; padding: 6px 40px 3px; }
  .champ .val span { display: block; border-bottom: 1px solid #000; min-height: 15px; }

  /* --- Blocs --- */
  .bandeau { background: #c9d7ea; font-weight: 700; font-size: 10px; padding: 3px 7px;
             border: 1px solid #7f8fa6; border-bottom: none; }
  .boite { border: 1px solid #7f8fa6; padding: 7px; margin-bottom: 12px;
           line-height: 1.9; min-height: 46px; }
  .boite .champ-ligne { display: flex; }
  .boite .etiq { width: 66px; text-align: right; padding-right: 8px; font-weight: 700; }
  .boite .trait { flex: 1; border-bottom: 1px solid #000; max-width: 230px; }
  .duo { display: flex; margin-bottom: 12px; }
  .duo > div { flex: 1; min-width: 0; }
  .duo .bandeau, .duo .boite { margin-bottom: 0; }
  .duo > div:first-child .bandeau, .duo > div:first-child .boite { border-right: none; }
  .souligne { border-bottom: 1px solid #000; display: inline-block; min-width: 220px; }

  /* --- Tableau articles --- */
  table.articles { width: 100%; border-collapse: collapse; }
  table.articles th { background: #c9d7ea; border: 1px solid #7f8fa6; padding: 4px;
                      font-size: 10px; }
  table.articles td { border: 1px solid #7f8fa6; padding: 4px 6px; height: 22px; }
  table.articles .c { width: 54px; text-align: center; }
  table.articles th:nth-child(3), table.articles th:nth-child(4) { width: 92px; }
  .m { text-align: right; }

  /* --- Pied du tableau : hrs / taxes / totaux --- */
  .pied { display: flex; align-items: flex-start; }
  .hrs { width: 54px; border: 1px solid #7f8fa6; border-top: none; padding: 4px 6px;
         font-weight: 600; }
  .taxes { flex: 1; text-align: right; padding: 4px 10px 0 0; font-weight: 700;
           line-height: 1.85; }
  table.totaux { border-collapse: collapse; width: 184px; }
  table.totaux td { border: 1px solid #7f8fa6; padding: 3px 7px; }
  table.totaux .lbl { background: #c9d7ea; font-weight: 700; font-size: 10px;
                      text-align: right; }
  table.totaux .val { text-align: right; width: 92px; }
  table.totaux .fort td { font-weight: 700; font-size: 12px; }

  .heures { margin-top: 18px; line-height: 2.3; font-weight: 700; }
  .heures .t { display: inline-block; border-bottom: 1px solid #000; width: 84px;
               margin: 0 16px 0 4px; }

  /* --- Pages 2 et 3 --- */
  .cadre-page { border: 1px solid #000; padding: 14px 16px; margin-bottom: 16px; }
  .cadre-page h3 { font-size: 11px; margin: 0 0 3px; }
  .cadre-page h3:not(:first-child) { margin-top: 14px; }
  .cadre-page p { margin: 0 0 6px; line-height: 1.6; }
  .ligne-vide { border-bottom: 1px solid #000; height: 15px; margin: 4px 0 10px; }
  .case { margin: 3px 0; line-height: 1.7; }
  .titre-noir { background: #6b6b6b; color: #fff; text-align: center; font-weight: 700;
                font-size: 10px; padding: 3px; letter-spacing: 0.5px; }
  .surligne { background: #f7f0a0; display: inline-block; min-width: 210px;
              border-bottom: 1px solid #000; }
  table.exceptions { width: 100%; border-collapse: collapse; margin-top: 6px; }
  table.exceptions td { padding: 2px 0; }
  table.exceptions .col { width: 100px; text-align: center; }
  table.exceptions .col span { display: inline-block; border-bottom: 1px solid #000;
                               width: 68px; }
  .cgv { line-height: 1.55; text-align: justify; }
  .cgv b { font-weight: 700; }
  .interne { display: flex; gap: 26px; }
  .interne > div { flex: 1; }
  .tarifs td { padding: 2px 14px 2px 0; }

  @media print {
    body { padding: 0; }
    .page { margin-bottom: 0; page-break-after: always; }
    .page:last-child { page-break-after: auto; }
    .page + .page { border-top: none; padding-top: 0; }
    @page { margin: 12mm; }
  }
</style>
</head>
<body>

<!-- ================= PAGE 1 — BON DE TRAVAIL ================= -->
<div class="page">
  <header>
    <div>
      ${
        societe.logo
          ? `<img class="logo" src="${societe.logo}" alt="${societe.name}">`
          : `<div class="marque">${societe.name}</div>`
      }
      ${societe.slogan ? `<div class="slogan">${societe.slogan}</div>` : ""}
      <div class="coord">
        ${societe.address ? `${societe.address}<br>` : ""}
        ${societe.phone ? `${societe.phone}<br>` : ""}
        ${societe.email || ""}
      </div>
    </div>
    <div class="droite">
      <h1>BON DE TRAVAIL</h1>
      <div class="champ">
        <div class="lbl">BON DE TRAVAIL #</div>
        <div class="val"><span>${numeroBon || "—"}</span></div>
      </div>
      <div class="champ">
        <div class="lbl">DATE</div>
        <div class="val"><span>${dateBon}</span></div>
      </div>
      <div class="champ">
        <div class="lbl">RÉFÉRENCE</div>
        <div class="val"><span>${reference || "&nbsp;"}</span></div>
      </div>
    </div>
  </header>

  <div class="bandeau">DÉTAILS DU CLIENT</div>
  <div class="boite">
    <div class="champ-ligne">
      <span class="etiq">Nom :</span>
      <span class="trait">${nomClient}</span>
    </div>
    <div class="champ-ligne">
      <span class="etiq">Contact :</span>
      <span class="trait">${telClient}${
    telClient && emailClient ? " · " : ""
  }${emailClient}</span>
    </div>
  </div>

  <div class="duo">
    <div>
      <div class="bandeau">ADRESSE D'ORIGINE</div>
      <div class="boite">
        <span class="souligne">${adresseOrigine || "&nbsp;"}</span>
      </div>
    </div>
    <div>
      <div class="bandeau">ADRESSE DE DESTINATION</div>
      <div class="boite">
        <span class="souligne">${adresseDestination || "&nbsp;"}</span>
      </div>
    </div>
  </div>

  <div class="bandeau">DESCRIPTION DU TRAVAIL</div>
  <div class="boite" style="min-height:62px">
    <span class="souligne">${descriptionTravail || "&nbsp;"}</span>
    ${dateService ? `<br>Date prévue : ${dateService}` : ""}
  </div>

  <table class="articles">
    <thead>
      <tr><th>QTÉ</th><th>DESCRIPTION</th><th>PRIX UNITÉ</th><th>LIGNE TOTAL</th></tr>
    </thead>
    <tbody>
      ${ligneArticle(
        num(form.tempsDeplacement),
        "Temps de travail",
        num(form.prixParHeure),
        tempsTravail
      )}
      ${ligneArticle(
        num(form.heuresDeplacement) > 0 ? num(form.heuresDeplacement) : "",
        "Temps de déplacement",
        num(form.heuresDeplacement) > 0 ? taux : null,
        coutDeplacement > 0 ? coutDeplacement : null
      )}
      ${ligneArticle(
        "",
        "Frais de déplacement",
        null,
        num(form.fraisDeplacement) > 0 ? num(form.fraisDeplacement) : null
      )}
      ${ligneArticle(
        "",
        "Frais de carburant",
        null,
        num(form.fraisCarburant) > 0 ? num(form.fraisCarburant) : null
      )}
      ${ligneArticle(nbSacs > 0 ? nbSacs : "", "Sac à matelas", null, null)}
      ${ligneVide()}${ligneVide()}${ligneVide()}
    </tbody>
  </table>

  <div class="pied">
    <div class="hrs">hrs : ${num(form.tempsDeplacement) || ""}</div>
    <div class="taxes"></div>
    <table class="totaux">
      <tr class="fort"><td class="lbl">TOTAL</td><td class="val">${fmt(total)} $</td></tr>
    </table>
  </div>

  <div class="heures">
    Heure début :<span class="t"></span>Initiales :<span class="t"></span><br>
    Heure fin :<span class="t"></span>Initiales :<span class="t"></span>
  </div>
</div>

<!-- ============ PAGE 2 — EXCEPTIONS ET CONFIRMATION ============ -->
<div class="page">
  <div class="cadre-page">
    <h3>1. REFUS DE PROTECTION PAR LE CLIENT</h3>
    <p>Le client refuse les protections recommandées (couvertures, emballage ou protection)
       pour les articles suivants :</p>
    <div class="ligne-vide"></div>
    <div class="case">☐ <b>Le client comprend que les articles déplacés sans protection sont
       transportés à ses propres risques.</b></div>

    <h3>2. DOMMAGES PRÉEXISTANTS</h3>
    <p>Les articles suivants présentaient déjà des dommages avant la manutention :</p>
    <div class="ligne-vide"></div>
    <div class="case">☐ <b>Photos prises avant la manutention.</b></div>

    <h3>3. ARTICLES SPÉCIAUX / DE GRANDE VALEUR</h3>
    <p>Articles nécessitant une manutention particulière (marbre, antiquités, coffre-fort,
       œuvres d'art, etc.) :</p>
    <div class="ligne-vide"></div>
    <div class="case">☐ <b>Manipulation effectuée selon les directives du client.</b></div>
    <div class="case">☐ <b>Service de déménageurs spécialisés recommandé et refusé par le client.</b></div>

    <h3>4. ARTICLES SURDIMENSIONNÉS (NO-FIT) / NON PASSABLES</h3>
    <p>Articles trop volumineux pour passer de façon sécuritaire dans les portes, escaliers
       ou corridors :</p>
    <div class="ligne-vide"></div>

    <h3>5. EXCEPTIONS</h3>
    <table class="exceptions">
      <tr><td></td><td class="col"><b>Origin</b></td><td class="col"><b>Destination</b></td></tr>
      <tr><td>☐ Longue distance entre le camion et l'entrée</td>
          <td class="col"><span></span></td><td class="col"><span></span></td></tr>
      <tr><td>☐ Accès limité pour le camion</td>
          <td class="col"><span></span></td><td class="col"><span></span></td></tr>
      <tr><td>☐ Contraintes liées aux escaliers / ascenseur non-réservé</td>
          <td class="col"><span></span></td><td class="col"><span></span></td></tr>
      <tr><td>☐ Camion laissé ouvert sans surveillance</td>
          <td class="col"><span></span></td><td class="col"><span></span></td></tr>
      <tr><td>☐ Client / représentant absent</td>
          <td class="col"><span></span></td><td class="col"><span></span></td></tr>
      <tr><td>☐ Temps d'attente sur place</td>
          <td class="col"><span></span></td><td class="col"><span></span></td></tr>
    </table>
  </div>

  <div style="border:1px solid #000">
    <div class="titre-noir">CETTE SECTION EST OBLIGATOIRE</div>
    <div style="padding:12px 16px">
      <div style="line-height:2"><b>Nom du client :</b>
        <span class="surligne">${nomClient}</span></div>
      <div style="line-height:2"><b>Numéro bon travail :</b>
        <span class="surligne">${numeroBon || reference}</span></div>

      <h3 style="margin-top:14px">A. CONFIRMATION DE FIN DE CHARGEMENT / DÉCHARGEMENT</h3>
      <p>Le client confirme que le chargement est complété, que tout le contenu prévu a été
         chargé, que le déchargement est complété et que le camion est vide.</p>

      <h3>B. CERTIFICAT DE DOMMAGES À LA PROPRIÉTÉ</h3>
      <p>Le soussigné reconnaît que le chauffeur, en compagnie du client ou de son
         représentant, a vérifié l'ensemble des pièces et des lieux de la résidence afin de
         s'assurer de l'état des lieux à la fin du déménagement.</p>

      <div style="display:flex;margin-top:12px">
        <div style="width:110px;font-weight:700">ORIGIN :</div>
        <div>
          <div class="case">☐ Chargement complété (le client confirme que tout le contenu
              prévu a été chargé)</div>
          <div class="case">☐ Aucun dommage à la propriété</div>
        </div>
      </div>
      <div style="display:flex;margin-top:10px">
        <div style="width:110px;font-weight:700">DESTINATION :</div>
        <div>
          <div class="case">☐ Déchargement complété (le client confirme que le camion est vide)</div>
          <div class="case">☐ Aucun dommage à la propriété</div>
        </div>
      </div>

      <div style="text-align:right;margin-top:22px">
        <b>Signature :</b> <span class="surligne">&nbsp;</span>
      </div>
    </div>
  </div>
</div>

<!-- ============ PAGE 3 — TERMES ET CONDITIONS ============ -->
<div class="page">
  <div class="bandeau" style="border-bottom:1px solid #7f8fa6">TERMES ET CONDITIONS</div>
  <div class="cadre-page cgv" style="border-top:none;margin-top:-1px">
    <p><b>1. Généralités</b> — Le client atteste être le propriétaire légal des biens ou avoir
      l'autorisation de les déplacer. Il est responsable de leur inventaire et inspection avant
      et après le déménagement. Les articles dépassant 225 lbs pourraient être assujettis à une
      surcharge de poids égale à 1 $ le lbs dépassant 225 lbs.</p>
    <p><b>2. Tarification et Paiement</b> — Facturation à l'heure, dès l'arrivée de l'équipe
      jusqu'à la fin du déchargement, par tranches de 15 minutes (minimum 3 heures, sauf
      indication contraire). Frais de déplacement et carburant en supplément. Paiement en
      espèces, carte de crédit ou virement Interac. La soumission est une estimation. Le prix
      final peut être ajusté en fonction de la durée réelle et des services additionnels. Le
      déménageur peut retenir les biens tant que le paiement intégral n'est pas effectué.</p>
    <p><b>3. Dommages et Réparations</b> — Le déménageur prend toutes les précautions
      nécessaires mais ne garantit pas l'absence totale de dommages. En cas de dommage, il
      tentera une réparation ou appliquera un plafond de dédommagement de 2,50 $/lb si la
      réparation est impossible. Réclamation sous 24 heures après le déménagement, franchise de
      250 $ applicable. Le client est responsable de protéger ses planchers, murs, escaliers et
      cadres de portes. Le déménageur n'est pas responsable des dommages accidentels à ces
      surfaces. Les téléviseurs doivent être emballés dans leur boîte d'origine ou une boîte
      spécialisée.</p>
    <p><b>4. Exclusions de Responsabilité</b> — Le déménageur n'est pas responsable de : perte
      ou vol d'argent, bijoux, documents, objets de valeur. Dommages aux articles non emballés
      par nos soins. Dommages aux matériaux fragiles (miroirs, verre, marbre, granit, quartz,
      céramique, etc.). Dysfonctionnement d'appareils électroniques ou électroménagers. Dommages
      aux meubles en mélamine, bois pressé ou démontables. Dommages aux planchers, murs,
      escaliers, ascenseurs et parties communes dus aux contraintes physiques du déménagement.
      Dommages aux plantes naturelles ou artificielles. Aucun branchement d'entrée d'eau
      (lave-vaisselle, frigo, laveuse). Ce travail est réservé aux techniciens licenciés. Le
      déménageur n'est pas responsable de tout dommage découlant d'une utilisation inappropriée
      de nos services.</p>
    <p><b>5. Réclamations et Limitations</b> — Responsabilité limitée à 2,50 $/lb en cas de
      dommage non réparable. Aucune réclamation acceptée après 24 heures. Le paiement total est
      obligatoire avant toute réclamation.</p>
    <div style="margin-top:26px"><b>Signature :</b>
      <span class="souligne">&nbsp;</span></div>
  </div>

  ${
    avecInterne
      ? `<div class="bandeau" style="border-bottom:1px solid #7f8fa6">RÉSERVÉ À L'USAGE INTERNE</div>
  <div class="cadre-page" style="border-top:none;margin-top:-1px">
    <div class="interne">
      <div>
        <div><b>DÉTAILS TARIF :</b></div>
        <div style="margin-top:16px"><b>NO. CAMION :</b> <span class="souligne"
             style="min-width:90px">&nbsp;</span></div>
        <div style="margin-top:16px"><b>ÉQUIPE :</b> Lead <span class="souligne"
             style="min-width:120px">&nbsp;</span></div>
        <div style="margin-top:6px;font-size:10px;line-height:2.4">
          <span class="souligne" style="min-width:150px">&nbsp;</span><br>
          <span class="souligne" style="min-width:150px">&nbsp;</span><br>
          <span class="souligne" style="min-width:150px">&nbsp;</span>
        </div>
      </div>
      <div>
        <div><b>MODE</b></div>
        <div class="case">☐ Comptant &nbsp;&nbsp; ☐ Chèque</div>
        <div class="case">☐ Carte Crédit &nbsp;&nbsp; ☐ À Facturer</div>
        <div class="case">☐ Interac (Réponse : ${INTERAC})</div>
        <div style="margin-top:14px"><b>COMMENTAIRES :</b></div>
        <div class="ligne-vide"></div>
        <div class="ligne-vide"></div>
      
      </div>
    </div>
  </div>`
      : ""
  }
</div>

</body>
</html>`;

  // ============================================================
  // IMPRIMER (ou enregistrer en PDF depuis la boîte d'impression)
  // ============================================================
  const handlePrint = () => {
    if (!valider()) return;

    const fenetre = window.open("", "_blank", "width=900,height=1000");
    if (!fenetre) {
      setErreur(
        "Le navigateur a bloqué la fenêtre d'impression. Autorisez les pop-ups pour ce site."
      );
      return;
    }
    fenetre.document.open();
    fenetre.document.write(documentHtml(true));
    fenetre.document.close();
    fenetre.focus();
    setTimeout(() => fenetre.print(), 400);
  };

  // ============================================================
  // ENREGISTRER + ENVOYER PAR EMAIL (via le backend)
  // ============================================================
  const handleSave = async () => {
    if (!valider()) return;

    setSaving(true);
    setErreur("");
    try {
      const res = await fetch(`${apiBaseUrl}/demandes/${demande.id}/devis`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          prixParHeure: num(form.prixParHeure),
          tempsDeplacement: num(form.tempsDeplacement),
          heuresDeplacement: num(form.heuresDeplacement),
          fraisCarburant: num(form.fraisCarburant),
          fraisDeplacement: num(form.fraisDeplacement),
          sacMatelas: nbSacs,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "La mise à jour du devis a échoué.");
      }

      onSaved?.();
    } catch (e) {
      setErreur(e.message || "La mise à jour du devis a échoué.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              💰 Bon de travail{" "}
              {numeroBon ? `n° ${numeroBon}` : `réf. ${reference}`}
            </h2>
            <p className="mt-0.5 text-sm text-gray-500">
              {nomClient || "Client"}
              {emailClient ? ` · ${emailClient}` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-2xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          {CHAMPS.map((champ) => (
            <div key={champ.name}>
              <label
                htmlFor={champ.name}
                className="mb-1 block text-sm font-semibold text-gray-700"
              >
                {champ.label}
                {champ.requis && <span className="text-red-500"> *</span>}
              </label>
              <div className="relative">
                <input
                  id={champ.name}
                  name={champ.name}
                  type="number"
                  min="0"
                  step={champ.step}
                  value={form[champ.name]}
                  onChange={handleChange}
                  placeholder="0.00"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-20 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-400">
                  {champ.unite}
                </span>
              </div>
              {champ.aide && <p className="mt-1 text-xs text-gray-500">{champ.aide}</p>}
            </div>
          ))}

          <div className="rounded-xl bg-gray-50 p-4 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>
                Temps de travail ({fmt(form.prixParHeure)} $/h × {num(form.tempsDeplacement)} h)
              </span>
              <span>{fmt(tempsTravail)} $</span>
            </div>
            {coutDeplacement > 0 && (
              <div className="mt-1 flex justify-between text-gray-600">
                <span>
                  Temps de déplacement ({fmt(taux)} $/h ×{" "}
                  {num(form.heuresDeplacement)} h)
                </span>
                <span>{fmt(coutDeplacement)} $</span>
              </div>
            )}
            {num(form.fraisDeplacement) > 0 && (
              <div className="mt-1 flex justify-between text-gray-600">
                <span>Frais de déplacement</span>
                <span>{fmt(form.fraisDeplacement)} $</span>
              </div>
            )}
            {num(form.fraisCarburant) > 0 && (
              <div className="mt-1 flex justify-between text-gray-600">
                <span>Frais de carburant</span>
                <span>{fmt(form.fraisCarburant)} $</span>
              </div>
            )}
            {nbSacs > 0 && (
              <div className="mt-1 flex justify-between text-gray-500">
                <span>Sac à matelas (× {nbSacs})</span>
                <span className="italic"></span>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between border-t-2 border-gray-400 pt-3">
              <span className="font-bold text-gray-900">TOTAL</span>
              <span className="text-2xl font-bold text-blue-600">{fmt(total)} $</span>
            </div>
          </div>

          {erreur && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {erreur}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 border-t border-gray-200 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handlePrint}
            disabled={saving}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            🖨️ Imprimer (3 pages)
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? "Envoi en cours…" : "✅ Enregistrer & envoyer par email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DevisDetailModal;